import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import RefreshIcon from '@mui/icons-material/Refresh'
import AssignmentIcon from '@mui/icons-material/Assignment'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { crqDeploymentApi } from '../utils/api'

const CRQ_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING_CREATION', label: 'Pending Creation' },
  { value: 'CREATED', label: 'Created' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'ELAPSED', label: 'Elapsed' },
]

const ENVIRONMENTS = [
  { value: '', label: 'All Environments' },
  { value: 'dev', label: 'DEV' },
  { value: 'qa', label: 'QA' },
  { value: 'uat', label: 'UAT' },
  { value: 'prd', label: 'PRD' },
]

const formatStatus = (status) => {
  const statusMap = {
    PENDING_CREATION: 'Pending Creation',
    CREATED: 'Created',
    PENDING_APPROVAL: 'Pending Approval',
    APPROVED: 'Approved',
    SCHEDULED: 'Scheduled',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
    ELAPSED: 'Elapsed',
  }
  return statusMap[status] || status
}

const getStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'success'
    case 'IN_PROGRESS':
    case 'APPROVED':
    case 'SCHEDULED':
      return 'info'
    case 'PENDING_CREATION':
    case 'PENDING_APPROVAL':
    case 'CREATED':
      return 'warning'
    case 'FAILED':
    case 'CANCELLED':
    case 'ELAPSED':
      return 'error'
    default:
      return 'default'
  }
}

const getRenewalStatusColor = (status) => {
  switch (status) {
    case 'RENEWED':
    case 'PUSHED':
      return 'success'
    case 'RENEWING':
      return 'info'
    case 'PENDING':
      return 'warning'
    case 'FAILED':
      return 'error'
    case 'SKIPPED':
      return 'default'
    default:
      return 'default'
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString()
}

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString()
}

const CRQs = () => {
  const [crqDeployments, setCrqDeployments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rowCount, setRowCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [expandedRowIds, setExpandedRowIds] = useState(new Set())

  // Filters
  const [statusFilter, setStatusFilter] = useState('')
  const [environmentFilter, setEnvironmentFilter] = useState('')

  const fetchCRQs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        page: paginationModel.page + 1, // Backend is 1-indexed
        page_size: paginationModel.pageSize,
      }
      if (statusFilter) params.status = statusFilter
      if (environmentFilter) params.environment = environmentFilter

      const response = await crqDeploymentApi.getAll(params)
      const { items, total } = response.data

      const mapped = items.map((crq) => ({
        id: crq.crq_id,
        crqNumber: crq.crq_number || '—',
        crqType: crq.crq_type || 'RENEWAL',
        productCode: crq.product_code,
        productName: crq.product_name || crq.product_code,
        environment: crq.environment,
        status: crq.status,
        scheduledStart: crq.scheduled_start_date,
        scheduledEnd: crq.scheduled_end_date,
        windowStart: crq.window_start,
        windowEnd: crq.window_end,
        coordinatorEmail: crq.coordinator_email || '—',
        crqCreatedAt: crq.crq_created_at,
        renewalStartedAt: crq.renewal_started_at,
        renewalCompletedAt: crq.renewal_completed_at,
        reminderCount: crq.reminder_count || 0,
        errorMessage: crq.error_message,
        createdAt: crq.created_at,
        updatedAt: crq.updated_at,
        certificates: crq.certificates || [],
        certCount: (crq.certificates || []).length,
      }))

      setCrqDeployments(mapped)
      setRowCount(total)
    } catch (err) {
      console.error('Error fetching CRQ deployments:', err)
      setError(err.response?.data?.detail || 'Failed to load CRQ deployments. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [paginationModel, statusFilter, environmentFilter])

  useEffect(() => {
    fetchCRQs()
  }, [fetchCRQs])

  const toggleRow = (id) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const renderCellWithWrap = (params) => {
    const value = params.value || ''
    const shouldWrap = String(value).length > 40

    return (
      <Box
        sx={{
          whiteSpace: shouldWrap ? 'normal' : 'nowrap',
          wordWrap: shouldWrap ? 'break-word' : 'normal',
          lineHeight: '1.5',
          padding: '8px 0',
        }}
      >
        {value}
      </Box>
    )
  }

  const columns = [
    {
      field: 'expand',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            toggleRow(params.row.id)
          }}
          aria-label={expandedRowIds.has(params.row.id) ? 'Collapse certificates' : 'Expand certificates'}
        >
          {expandedRowIds.has(params.row.id) ? (
            <KeyboardArrowUpIcon />
          ) : (
            <KeyboardArrowDownIcon />
          )}
        </IconButton>
      ),
    },
    {
      field: 'crqNumber',
      headerName: 'CRQ Number',
      minWidth: 150,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'crqType',
      headerName: 'Type',
      minWidth: 110,
      renderCell: (params) => (
        <Chip
          label={params.value === 'LAYER7' ? 'Layer7' : 'Renewal'}
          size="small"
          color={params.value === 'LAYER7' ? 'secondary' : 'primary'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'productName',
      headerName: 'Product',
      minWidth: 180,
      flex: 1,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'environment',
      headerName: 'Environment',
      minWidth: 110,
      renderCell: (params) => (
        <Chip
          label={params.value?.toUpperCase()}
          size="small"
          color={params.value === 'prd' ? 'error' : 'default'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 150,
      renderCell: (params) => (
        <Chip
          label={formatStatus(params.value)}
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'certCount',
      headerName: 'Certs',
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'scheduledStart',
      headerName: 'Scheduled Start',
      minWidth: 160,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'scheduledEnd',
      headerName: 'Scheduled End',
      minWidth: 160,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'windowStart',
      headerName: 'Expiry Window Start',
      minWidth: 130,
      renderCell: (params) => formatDateShort(params.value),
    },
    {
      field: 'windowEnd',
      headerName: 'Expiry Window End',
      minWidth: 130,
      renderCell: (params) => formatDateShort(params.value),
    },
    {
      field: 'coordinatorEmail',
      headerName: 'Coordinator',
      minWidth: 200,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'reminderCount',
      headerName: 'Reminders',
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      minWidth: 160,
      renderCell: (params) => formatDate(params.value),
    },
  ]

  // Custom detail panel rendered below expanded rows
  const DetailPanel = ({ row }) => {
    const certs = row.certificates || []
    if (certs.length === 0) {
      return (
        <Box sx={{ p: 2, pl: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No certificates linked to this CRQ.
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ p: 2, pl: 8, pr: 4, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Certificates ({certs.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Common Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Venafi Cert ID (DN)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cert GUID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Renewal Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Venafi Expiration</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Renewed At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Pushed At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {certs.map((cert) => (
                <TableRow key={cert.id} hover>
                  <TableCell>{cert.common_name || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', maxWidth: 320 }}>
                    <Tooltip title={cert.venafi_cert_id || '—'}>
                      <span>{cert.venafi_cert_id || '—'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {cert.cert_id}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cert.renewal_status}
                      size="small"
                      color={getRenewalStatusColor(cert.renewal_status)}
                    />
                  </TableCell>
                  <TableCell>{formatDateShort(cert.venafi_expiration_date)}</TableCell>
                  <TableCell>{formatDate(cert.renewed_at)}</TableCell>
                  <TableCell>{formatDate(cert.pushed_at)}</TableCell>
                  <TableCell>
                    {cert.error_message ? (
                      <Tooltip title={cert.error_message}>
                        <Chip label="Error" size="small" color="error" variant="outlined" />
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {row.errorMessage && (
          <Alert severity="error" sx={{ mt: 1 }}>
            CRQ Error: {row.errorMessage}
          </Alert>
        )}
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            CRQ Deployments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchCRQs} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPaginationModel((prev) => ({ ...prev, page: 0 }))
          }}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {CRQ_STATUSES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Environment"
          value={environmentFilter}
          onChange={(e) => {
            setEnvironmentFilter(e.target.value)
            setPaginationModel((prev) => ({ ...prev, page: 0 }))
          }}
          size="small"
          sx={{ minWidth: 160 }}
        >
          {ENVIRONMENTS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={crqDeployments}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={rowCount}
            paginationMode="server"
            loading={loading}
            autoHeight
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            getRowHeight={() => 'auto'}
            onRowClick={(params) => toggleRow(params.row.id)}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                paddingTop: '8px',
                paddingBottom: '8px',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: '1.2',
                fontWeight: 'bold',
              },
            }}
          />

          {/* Expanded detail panels rendered below the grid */}
          {crqDeployments
            .filter((row) => expandedRowIds.has(row.id))
            .map((row) => (
              <Collapse key={row.id} in={true}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, bgcolor: 'primary.main', color: 'white', borderRadius: '4px 4px 0 0' }}>
                    <Typography variant="subtitle2">
                      {row.crqNumber} — {row.productName} ({row.environment?.toUpperCase()})
                    </Typography>
                  </Box>
                  <DetailPanel row={row} />
                </Box>
              </Collapse>
            ))}
        </Box>
      </Paper>
    </Box>
  )
}

export default CRQs
